import { Button } from "@/components/ui/UIElements/Button";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon, Modal, Switch } from "../../";
import { useBusinessToggle } from "../../../../hooks";

const BusinessCard = ({ business }) => {
  const { t } = useTranslation("navigation");
  const navigate = useNavigate();
  const toggleBusinessMutation = useBusinessToggle();
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  const { id, name, logo, enabled, city } = business;

  const hideStatus = true;

  const getStatusColor = () => {
    return enabled
      ? "bg-success/20 text-success border-success/30"
      : "bg-destructive/20 text-destructive border-destructive/30";
  };

  const getBorderColor = () => {
    return enabled ? "border-success/50" : "border-destructive/50";
  };

  const getStatusText = () => {
    return enabled ? "Active" : "Inactive";
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    navigate(`/menu/stores/${id}`);
  };

  const handleToggleBusiness = (checked) => {
    if (checked) {
      handleConfirmToggle();
    } else {
      setShowDeactivateModal(true);
    }
  };

  const handleConfirmToggle = async () => {
    try {
      const newEnabledStatus = !enabled;

      await toggleBusinessMutation.mutateAsync({
        businessId: id,
        enabled: newEnabledStatus,
      });

      setShowDeactivateModal(false);
    } catch (error) {
      console.error("Toggle business status failed:", error);
    }
  };

  return (
    <>
      <div
        className={`bg-background hover:bg-card rounded-lg border-2 p-4 transition-colors ${getBorderColor()}`}
      >
        {/* Business Info */}
        <div className="space-y-2">
          {/* Logo and Name */}
          <div className="flex items-start space-x-3">
            {logo ? (
              <img
                src={logo}
                alt={`${name} logo`}
                className="h-12 w-12 flex-shrink-0 rounded-lg object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <div className="bg-card flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg">
                <div className="text-muted-foreground text-xs">No logo</div>
              </div>
            )}

            <div className="min-w-0 flex-1">
              <h3 className="line-clamp-2 text-sm leading-tight font-semibold text-white">
                {name}
              </h3>
              <p className="text-muted-foreground mt-1 text-xs">
                {city?.name || "Unknown City"}
              </p>
            </div>
          </div>

          {/* Business Status */}
          {!hideStatus && (
            <div className="flex items-center space-x-2">
              <span className="text-muted-foreground text-xs">
                Business Status:
              </span>
              <div
                className={`rounded-full border px-2 py-1 text-xs font-medium ${getStatusColor()}`}
              >
                {getStatusText()}
              </div>
            </div>
          )}
          {/* POS Integrations Link */}
          <div className="border-border mt-3 border-t pt-3">
            <Button
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/integrations/pos", { state: { business } });
              }}
              className="border-primary/30 bg-primary/20 hover:bg-primary/30 flex w-full items-center justify-center space-x-2 rounded-md border px-3 py-2 text-xs font-medium text-[var(--od-action-content)] transition-colors"
            >
              <Icon name="Link" className="h-3 w-3" />
              <span>View POS Systems</span>
            </Button>
            <Button
              variant="ghost"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/growth/link-in-bio");
              }}
              className="border-primary/30 bg-primary/20 hover:bg-primary/30 mt-2 flex w-full items-center justify-center space-x-2 rounded-md border px-3 py-2 text-xs font-medium text-[var(--od-action-content)] transition-colors"
            >
              <Icon name="Link2" className="h-3 w-3" />
              <span>{t("businessCard.manageLinkInBio")}</span>
            </Button>
          </div>
        </div>

        {/* Footer - Actions */}
        <div className="border-border mt-2 border-t pt-3">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handleEditClick}
              className="bg-card text-foreground hover:bg-secondary rounded px-2 py-1 text-xs transition-colors"
            >
              Edit
            </Button>
            <div className="flex items-center gap-2">
              <span
                id={`business-${id}-status`}
                className="text-muted-foreground text-xs"
              >
                {enabled ? "Enabled" : "Disabled"}
              </span>
              <Switch
                aria-labelledby={`business-${id}-status`}
                checked={enabled}
                onCheckedChange={handleToggleBusiness}
                disabled={toggleBusinessMutation.isPending}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Deactivate Business Confirmation Modal */}
      <Modal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        title="Deactivate Store"
        subtitle="Are you sure you want to deactivate this store?"
        maxWidth="max-w-md"
        className="ordering-catalog-surface"
      >
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="bg-destructive/20 flex h-10 w-10 items-center justify-center rounded-full">
                <Icon
                  name="AlertTriangle"
                  className="text-destructive h-5 w-5"
                />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-white">
                Deactivate {name}?
              </h3>
              <p className="text-muted-foreground mt-1 text-sm">
                This store will be disabled and may affect its operations. You
                can reactivate it later if needed.
              </p>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <Button
              variant="ghost"
              type="button"
              onClick={handleConfirmToggle}
              disabled={toggleBusinessMutation.isPending}
              aria-busy={toggleBusinessMutation.isPending}
              className="bg-destructive hover:bg-destructive/90 flex flex-1 items-center justify-center space-x-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              {toggleBusinessMutation.isPending ? (
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
              ) : null}
              <span>
                {toggleBusinessMutation.isPending
                  ? "Deactivating..."
                  : "Deactivate Store"}
              </span>
            </Button>
            <Button
              variant="ghost"
              type="button"
              onClick={() => setShowDeactivateModal(false)}
              disabled={toggleBusinessMutation.isPending}
              autoFocus
              className="border-border bg-card text-foreground hover:bg-secondary flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default BusinessCard;
